const auth0ProxyUrl = window.location.hostname === 'localhost' ?
  'http://localhost:8080/api/v1/authManagementProxy' : '/api/v1/authManagementProxy';
const postPutHeaders = {
  'X-Requested-With': 'XMLHttpRequest',
  'Content-type': 'application/json',
};
const getHeaders = {
  'X-Requested-With': 'XMLHttpRequest',
};

export const createAuthUser = async (userData, token) => {
  // Temporary passwords are added by Cloud Proxy
  const headers = {
    ...postPutHeaders,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(`${auth0ProxyUrl}/user`,
    {
      body: JSON.stringify(userData),
      method: 'POST',
      headers: headers,
    },
  );

  if (!response.ok) {
    const { status, statusText } = response;

    throw new Error(`Create Auth0 user unsuccessful: ${statusText}, status ${status}`);
  }

  const createdUser = await response.json();

  return createdUser;
};

export const getUsersByEmail = async (email, token) => {
  const headers = {
    ...getHeaders,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(`${auth0ProxyUrl}/users/email/${email}`,
    {
      headers: headers,
    },
  );

  if (!response.ok) {
    const { status, statusText } = response;

    throw new Error(`Get Auth0 users by email unsuccessful: ${statusText}, status ${status}`);
  }

  const authUsers = await response.json();

  return authUsers;
};

export const getUsers = async (params, token) => {
  const queryString = Object.keys(params).length ? `?${new URLSearchParams(params)}` : '';
  const reqUrl = `${auth0ProxyUrl}/users${queryString}`;
  const headers = {
    ...getHeaders,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(reqUrl,
    {
      headers: headers,
    },
  );

  if (!response.ok) {
    const { status, statusText } = response;

    throw new Error(`Get Auth0 users unsuccessful: ${statusText}, status ${status}`);
  }

  const users = await response.json();

  return users;
};

export const updateAuthUser = async (authId, userData, token) => {
  if (authId.startsWith('google-oauth2|')) {
    delete userData.email;
    delete userData.connection;
  }

  const headers = {
    ...postPutHeaders,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(`${auth0ProxyUrl}/user/${authId}`,
    {
      body: JSON.stringify(userData),
      method: 'PATCH',
      headers: headers,
    },
  );

  if (!response.ok) {
    const { status, statusText } = response;

    throw new Error(`Update Auth0 user unsuccessful: ${statusText}, status ${status}`);
  }

  const updatedUser = await response.json();

  return updatedUser;
};

export const setAuthUserRoles = async (authId, userRoleData, token) => {
  const headers = {
    ...postPutHeaders,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(`${auth0ProxyUrl}/users/${authId}/roles`,
    {
      body: JSON.stringify(userRoleData),
      method: 'POST',
      headers: headers,
    },
  );

  if (!response.ok) {
    const { status, statusText } = response;

    throw new Error(`Setting Auth0 role(s) to user unsuccessful: ${statusText}, status ${status}`);
  }

  return;
};

export const removeAuthUserRoles = async (authId, userRoleData, token) => {
  const headers = {
    ...postPutHeaders,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(`${auth0ProxyUrl}/users/${authId}/roles`,
    {
      body: JSON.stringify(userRoleData),
      method: 'DELETE',
      headers: headers,
    },
  );

  if (!response.ok) {
    const { status, statusText } = response;

    throw new Error(
      `Removing Auth0 role(s) from user unsuccessful: ${statusText}, status ${status}`,
    );
  }

  return;
};

export const getAuthUserRoles = async (authId, token) => {
  const headers = {
    ...getHeaders,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(`${auth0ProxyUrl}/users/${authId}/roles`,
    {
      headers: headers,
    },
  );

  if (!response.ok) {
    const { status, statusText } = response;

    throw new Error(`Getting Auth0 user roles unsuccessful: ${statusText}, status ${status}`);
  }

  const authUserRoles = await response.json();

  return authUserRoles;
};

export const updateMetadataForSSOUser = async (externalSSOId, metadata, token) => {
  const { userMetadata, appMetadata } = metadata;

  if (externalSSOId.startsWith('auth0|')) {
    throw new Error(`Updating user's metadata unsuccessful: non-SSO account provided`);
  }

  const headers = {
    ...postPutHeaders,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(`${auth0ProxyUrl}/user/${externalSSOId}`,
    {
      body: JSON.stringify({
        user_metadata: userMetadata,
        app_metadata: appMetadata,
      }),
      method: 'PATCH',
      headers: headers,
    },
  );

  if (!response.ok) {
    const { status, statusText } = response;

    throw new Error(`Updating user's metadata unsuccessful: ${statusText}, status ${status}`);
  }

  const updatedUserWithMetadata = await response.json();

  return updatedUserWithMetadata;
};

export const linkUserIdentities = async (externalSSOId, secondaryIdentityData, token) => {
  const headers = {
    ...postPutHeaders,
    'Authorization': `Bearer ${token}`,
  };

  const response = await fetch(`${auth0ProxyUrl}/users/${externalSSOId}/linkIdentities`,
    {
      body: JSON.stringify(secondaryIdentityData),
      method: 'POST',
      headers: headers,
    },
  );

  if (!response.ok) {
    const { status, statusText } = response;

    throw new Error(`Linking user identities unsuccessful: ${statusText}, status ${status}`);
  }

  return;
};

export const searchUsers = async (params = {}, token) => {
  if (!Object.keys(params).length) {
    throw new Error('Invalid Search: No Params Found');
  }

  const headers = {
    ...getHeaders,
    'Authorization': `Bearer ${token}`,
  };

  const queryString = `?${new URLSearchParams(params)}`;
  const reqUrl = `${auth0ProxyUrl}/users/search${queryString}`;

  const response = await fetch(reqUrl,
    {
      headers: headers,
    },
  );

  if (!response.ok) {
    const { status, statusText } = response;

    throw new Error(`Search Auth0 users unsuccessful: ${statusText}, status ${status}`);
  }

  const users = await response.json();

  return users;
};
